import type { FC, ReactNode } from 'react';
import styled from 'styled-components';
import React, { useState, useRef, useCallback, useEffect, useMemo, useTransition } from 'react';
import { useResizeObserverWithRef } from '../../../lib/hooks/useResizeObserverWithRef';
import type { DOMRectReadOnly } from '../../../lib/hooks/useResizeObserver';
import { useMergedRefs } from '../../../lib/hooks/useMergedRefs';
import { useStableFunction } from '../../../lib/hooks/useStableFunction';
import { useIsRTLLayout } from '../../../appState/hooks/useIsRTLLayout';

export type LabelledToggleOption<T = string> = {
  label: ReactNode;
  selected?: boolean;
  value: T;
};

type LabelledToggleProps = {
  labels: LabelledToggleOption[];
  onSelect?: (selectedIndex: number) => void;
  fullWidth?: boolean;
};

export const LabelledToggle: FC<LabelledToggleProps> = ({ labels, onSelect, fullWidth }: LabelledToggleProps) => {
  const selectedIndex = labels.findIndex((option) => option.selected);
  const [highlightStyle, setHighlightStyle] = useState({ width: 0, left: 0 });
  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const updateHighlight = useCallback(() => {
    const selectedOption = optionRefs.current[selectedIndex];
    if (selectedOption) {
      const { offsetWidth: width, offsetLeft: left } = selectedOption;
      setHighlightStyle({ width, left });
    }
  }, [selectedIndex]);

  const isRtl = useIsRTLLayout();

  useEffect(() => {
    const t = setTimeout(() => updateHighlight(), 0);
    return () => {
      clearTimeout(t);
    };
  }, [updateHighlight, isRtl, labels, selectedIndex]);

  const [transitionPending, startTrans] = useTransition();

  return (
    <StyledLabelledToggle fullWidth={fullWidth}>
      <ToggleContainer>
        {labels.map((option: LabelledToggleOption, index: number) => (
          <ToggleOption
            key={option.value}
            ref={(el) => {
              optionRefs.current[index] = el;
            }}
            onClick={() => {
              startTrans(() => {
                if (onSelect) {
                  onSelect(index);
                }
              });
            }}
            isSelected={index === selectedIndex}
            onResizeObserved={updateHighlight}
          >
            {option.label}
          </ToggleOption>
        ))}
        <Highlight $pending={transitionPending} style={highlightStyle} />
      </ToggleContainer>
    </StyledLabelledToggle>
  );
};

const StyledLabelledToggle = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>`
  box-sizing: border-box;
  display: inline-flex;
  width: fit-content;
  height: 30px;
  padding: 0px;
  border: 1px solid #e3e8f3;
  background-color: #f5f7fc;
  border-radius: 12px;
  overflow: hidden;

  ${({ fullWidth }) =>
    fullWidth &&
    `
      width: 100%;

      > div {
        width: 100%;

        > div {
          flex: 1 1 0;
          text-align: center;
        }
      }
    `}
`;

const ToggleContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  gap: 6px;
`;

const ToggleOption = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof ToggleOptionComp> & { onResizeObserved?: (rect: DOMRectReadOnly | undefined) => void }
>(function ToggleOption({ children, onResizeObserved, ...props }, ref) {
  const masterRef = useWireUpResizeObservedCallback(onResizeObserved, ref);
  return (
    <ToggleOptionComp ref={masterRef} {...props}>
      {children}
    </ToggleOptionComp>
  );
});

function useWireUpResizeObservedCallback(
  onResizeObserved: ((rect: DOMRectReadOnly | undefined) => void) | undefined,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const [rect, rref] = useResizeObserverWithRef();
  const stableOnResizeObserved = useStableFunction(onResizeObserved);
  useEffect(() => {
    stableOnResizeObserved(rect);
  }, [stableOnResizeObserved, rect]);
  const refsToMerge = useMemo(() => [rref, ref], [rref, ref]);
  const masterRef = useMergedRefs(refsToMerge);
  return masterRef;
}

const ToggleOptionComp = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 14px;
  font-size: 12px;
  line-height: 1;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 500)};
  color: ${({ isSelected }) => (isSelected ? '#2f3443' : '#5f6785')};
  z-index: 1;
  position: relative;
  user-select: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
`;

const Highlight = styled.div<{ $pending: boolean }>`
  position: absolute;
  top: 0;
  height: 100%;
  background-color: ${({ $pending }) => ($pending ? '#fdfefe' : '#ffffff')};
  transition:
    width 0.3s ease,
    left 0.3s ease,
    right 0.3s ease /* In RTL mode, this is important */,
    background-color 0.3s ease;
  z-index: 0;
  box-shadow: 0 1px 3px rgba(31, 42, 68, 0.08);
`;
